import React, { FC, memo, useEffect, useRef } from 'react';
import { SharedIntersectionObserverContext } from './context';

export const SharedIntersectionObserverProvider: FC<{
    options: IntersectionObserverInit;
}> = memo(({ children, options }) => {
    const intersectionObserverRef = useRef(null);
    const entryMapRef = useRef(new Map<any, any>());
    useEffect(() => {
        const callbackFunction = entries => {
            entries.forEach(entry => {
                const callback = entryMapRef.current.get(entry.target);
                if (!callback) {
                    return;
                }
                callback(entry.isIntersecting);
            });
        };
        intersectionObserverRef.current = new IntersectionObserver(callbackFunction, options);
        entryMapRef.current.forEach((cb, ele) => intersectionObserverRef.current.observe(ele));
        return () => intersectionObserverRef.current.disconnect();
    }, [intersectionObserverRef, options]);
    const contextRef = useRef({
        addElement: (element, onVisible) => {
            entryMapRef.current.set(element, visible => onVisible(visible));
            if (!intersectionObserverRef.current) {
                return;
            }
            intersectionObserverRef.current.observe(element);
        },
        removeElement: element => {
            if (intersectionObserverRef.current) {
                intersectionObserverRef.current.unobserve(element);
            }
            entryMapRef.current.delete(element);
        },
        defaultVisible: false
    });
    return (
        <SharedIntersectionObserverContext.Provider value={contextRef.current}>
            {children}
        </SharedIntersectionObserverContext.Provider>
    );
});
